/* eslint-disable react/jsx-wrap-multilines */
import React, { useReducer } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { createInitialState, reducer } from './reducer';
import actionsCreator from './actionsCreator';

import ButtonLink from '../ButtonLink';
import ButtonIcon from '../ButtonIcon';
import Icon from '../Icon';
import Tags from '../Tags';
import Tag from '../Tag';
import TextInput from '../TextInput/TextInput';
import InputLabel from '../InputLabel';
import ValidationMessage from '../ValidationMessage';
import { getInputClassForValidationState } from '../ValidationMessage/helper';
import './InputSet.scss';

const InputSet = ({
  addMoreButtonText,
  buttonTooltipWarning,
  className,
  disableButtonTooltipWarning,
  initialValues,
  isDisabled,
  label,
  maxInputs,
  minInputs,
  placeholder,
  placeholders,
  tags,
  tooltipContent,
  tooltipPosition,
  validationFunction,
  validationMessage,
  validationState,

  buttonIconProps,
  buttonLinkProps,
  inputLabelProps,
  InputSetWrapperProps,
  tagProps,
  tagWrapperProps,
  textInputProps
}) => {
  if (minInputs > maxInputs) {
    throw new Error('minInputs must never be higher than maxInput');
  }

  const [state, dispatch] = useReducer(
    reducer,
    initialValues || createInitialState({ minInputs })
  );

  const actions = actionsCreator(dispatch);
  const { inputs } = state;

  const reachMaxInputs = inputs.length >= maxInputs;
  const reachMinInputs = inputs.length <= minInputs;
  const shouldDisplayButtonTooltip =
    !disableButtonTooltipWarning && reachMinInputs && !buttonTooltipWarning
      ? `There must be at least ${minInputs} of inputs`
      : null;

  const getFieldState = value => {
    switch (validationFunction && validationFunction(value)) {
      case 'success':
        return 'success';

      case 'warning':
        return 'warning';

      case 'error':
        return 'error';

      default:
        return undefined;
    }
  };

  return (
    <div
      className={classNames(
        'inputSet',
        className,
        getInputClassForValidationState(validationState)
      )}
      {...InputSetWrapperProps}
    >
      <InputLabel {...inputLabelProps}>{label}</InputLabel>
      {tags && (
        <Tags {...tagWrapperProps}>
          {tags.map(tag => (
            <Tag
              tagInnerClassName={classNames(
                inputs.includes(tag) && 'tags--light'
              )}
              disabled={isDisabled || maxInputs === inputs.length}
              className='flex-row center-xs'
              onClick={() => actions.selectTag(tag)}
              text={
                <>
                  <Icon variant='operator-plus' className='mr-1x' />
                  {tag}
                </>
              }
              key={tag}
              element='button'
              {...tagProps}
            />
          ))}
        </Tags>
      )}
      {inputs.map((value, index) => (
        <div
          className='flex-row middle-xs mt-1x textinput__wrapper'
          key={index}
        >
          <TextInput
            wrapperProps={{ className: 'w-100', isInputSet: true }}
            value={value}
            name={`input-${index}`}
            id={`id-${index}`}
            isDisabled={isDisabled}
            onChange={event => actions.editValue(index, event.target.value)}
            tooltipContent={!isDisabled ? tooltipContent : null}
            tooltipPosition={!isDisabled ? tooltipPosition : null}
            placeholder={(placeholders && placeholders[index]) || placeholder}
            validationState={getFieldState(value)}
            {...textInputProps}
          />
          <ButtonIcon
            className='flex-column'
            iconVariant='delete'
            onClick={() => actions.deleteInput(index)}
            disabled={reachMinInputs}
            title={
              disableButtonTooltipWarning &&
              reachMinInputs &&
              buttonTooltipWarning
                ? buttonTooltipWarning
                : shouldDisplayButtonTooltip
            }
            {...buttonIconProps}
          />
        </div>
      ))}
      {validationState && (
        <ValidationMessage validationState={validationState}>
          <p>{validationMessage}</p>
        </ValidationMessage>
      )}
      <ButtonLink
        onClick={actions.createInput}
        disabled={isDisabled}
        className={classNames('mt-1x', reachMaxInputs && 'hidden')}
        {...buttonLinkProps}
      >
        {addMoreButtonText}
      </ButtonLink>
    </div>
  );
};

InputSet.defaultProps = {
  addMoreButtonText: 'Add more',
  minInputs: 1,
  maxInputs: 10,
  isDisabled: false,
  validationMessage: 'Oeps, something went wrong here',
  tags: []
};

InputSet.propTypes = {
  addMoreButtonText: PropTypes.string,
  buttonIconProps: PropTypes.object,
  buttonLinkProps: PropTypes.object,
  buttonTooltipWarning: PropTypes.string,
  className: PropTypes.string,
  disableButtonTooltipWarning: PropTypes.bool,
  inputLabelProps: PropTypes.object,
  InputSetWrapperProps: PropTypes.object,
  initialValues: PropTypes.shape({
    inputs: PropTypes.arrayOf(PropTypes.string),
    numberOfInputs: PropTypes.number,
    arrayIndexes: PropTypes.number
  }),
  isDisabled: PropTypes.bool,
  label: PropTypes.string.isRequired,
  maxInputs: PropTypes.number,
  minInputs: PropTypes.number,
  placeholder: PropTypes.string,
  placeholders: PropTypes.arrayOf(PropTypes.string),
  tagProps: PropTypes.object,
  tags: PropTypes.arrayOf(PropTypes.string),
  tagWrapperProps: PropTypes.object,
  textInputProps: PropTypes.object,
  tooltipContent: PropTypes.string,
  tooltipPosition: PropTypes.string,
  validationFunction: PropTypes.func,
  validationMessage: PropTypes.string,
  validationState: PropTypes.string
};

export default InputSet;
